import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDelete, Field, PageHeader, Panel } from '@/components/kit';
import { FormDialog, ListBody, PickSelect, SwitchRow } from '@/components/inputs';
import {
  STATUS_LABELS,
  STATUS_PATH,
  STATUS_VALUES,
  loadAdminStatus,
  metaOf,
  overallOf,
  splitLinks,
} from '@/shared/status';
import { safeLink } from '@/shared/paymentSettings';
import { committed } from '@/shared/data';
import { formatRelativeTime } from '@/utils/date';
import { cn } from '@/lib/utils';

export const route = {
  path: '/admin/status',
  layout: 'owner',
  access: "['Admin_c','Staff_c'].includes(profile)",
};
export const nav = { icon: 'Activity', label: 'Status page', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 42 };

const EMPTY = { name: '', status: 'Up', message: '', links: '', sort: '', active: true };

/** Create, update or clear the private links row that belongs to a service. */
async function saveLinks(service, existing, text) {
  const urls = splitLinks(text).join('\n');
  const table = sdk.table('status_links_c');
  if (!urls) {
    if (!existing) return true;
    const res = await table.remove(existing.Id);
    return res?.success !== false;
  }
  const res = existing
    ? await table.update({ Id: existing.Id, Name: service.Name, urls_c: urls })
    : await table.create({ Name: service.Name, service_c: service.Id, urls_c: urls });
  return !!committed(res);
}

function ServiceDialog({ open, onOpenChange, service, nextSort, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      service
        ? {
            name: service.Name ?? '',
            status: service.status_c || 'Up',
            message: service.message_c ?? '',
            links: service.link?.urls_c ?? '',
            sort: service.sort_c ?? '',
            active: service.active_c !== false,
          }
        : { ...EMPTY, sort: String(nextSort) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const name = form.name.trim();

  async function submit() {
    if (!name) return;
    const sort = form.sort === '' ? null : Number(form.sort);
    const fields = {
      Name: name,
      status_c: form.status,
      message_c: form.message.trim(),
      sort_c: Number.isFinite(sort) ? sort : null,
      active_c: !!form.active,
    };
    setSaving(true);
    try {
      const res = service
        ? await sdk.table('status_services_c').update({ Id: service.Id, ...fields })
        : await sdk.table('status_services_c').create(fields);
      const saved = committed(res);
      if (!saved) return;
      const linksOk = await saveLinks({ Id: service?.Id ?? saved.Id, Name: name }, service?.link ?? null, form.links);
      if (linksOk) toast.success(`${name} saved`);
      else toast.warning(`${name} saved, but its links didn't save. Try editing it again.`);
      onOpenChange(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={service ? `Edit ${service.Name}` : 'Add a service'}
      description="Visitors see the name, status and note. Links stay private to you and staff."
      onSubmit={submit}
      saving={saving}
      submitLabel={service ? 'Save service' : 'Add service'}
      wide
    >
      <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
        <Field label="Service name" htmlFor="st-name" hint="What visitors see, e.g. Live channels.">
          <Input
            id="st-name"
            value={form.name}
            onChange={(e) => set('name')(e.target.value)}
            placeholder="Live channels"
            required
            maxLength={80}
          />
        </Field>
        <Field label="Status" htmlFor="st-status">
          <PickSelect id="st-status" value={form.status} onChange={set('status')} values={STATUS_VALUES} labels={STATUS_LABELS} />
        </Field>
      </div>
      <Field label="Links" htmlFor="st-links" hint="Private. One per line: servers, panels, playlist or EPG URLs you want to keep an eye on.">
        <Textarea
          id="st-links"
          rows={3}
          value={form.links}
          onChange={(e) => set('links')(e.target.value)}
          placeholder={'http://line.example.com:8080\nhttps://panel.example.com'}
          className="font-mono text-xs"
        />
      </Field>
      <Field label="Public note" htmlFor="st-message" hint="Optional. Shown under the service on the status page.">
        <Textarea
          id="st-message"
          rows={2}
          value={form.message}
          onChange={(e) => set('message')(e.target.value)}
          placeholder="Some channels are buffering. A fix is rolling out now."
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-[10rem_1fr] sm:items-start">
        <Field label="Order" htmlFor="st-sort" hint="Lower shows first.">
          <Input
            id="st-sort"
            type="number"
            inputMode="numeric"
            value={form.sort}
            onChange={(e) => set('sort')(e.target.value)}
            placeholder="10"
          />
        </Field>
        <SwitchRow
          id="st-active"
          label="Show on status page"
          hint="Switch off to hide this service from visitors without deleting it."
          checked={form.active}
          onChange={set('active')}
        />
      </div>
    </FormDialog>
  );
}

function LinkList({ text }) {
  const urls = splitLinks(text);
  if (!urls.length) return null;
  return (
    <ul className="mt-1.5 space-y-0.5">
      {urls.map((u) => {
        const href = safeLink(u);
        return (
          <li key={u} className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <ApperIcon name="Lock" size={12} className="shrink-0" />
            {href ? (
              <a href={href} target="_blank" rel="noreferrer noopener" className="truncate font-mono hover:text-primary hover:underline">
                {u}
              </a>
            ) : (
              <span className="truncate font-mono">{u}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ServiceRow({ service, onEdit, onChanged }) {
  const [saving, setSaving] = useState(false);
  const meta = metaOf(service.status_c);

  async function changeStatus(value) {
    if (value === service.status_c) return;
    setSaving(true);
    try {
      const res = await sdk.table('status_services_c').update({ Id: service.Id, status_c: value });
      if (committed(res, `${service.Name}: ${STATUS_LABELS[value]}`)) onChanged();
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (service.link) await sdk.table('status_links_c').remove(service.link.Id);
    const res = await sdk.table('status_services_c').remove(service.Id);
    if (res?.success === false) {
      committed(res);
      return false;
    }
    onChanged();
    return true;
  }

  return (
    <li className="flex flex-col gap-3 py-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', meta.dot)} aria-hidden="true" />
          {service.Name}
          {service.active_c === false && (
            <Badge variant="outline" className="text-muted-foreground">
              Hidden
            </Badge>
          )}
        </p>
        {service.message_c && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{service.message_c}</p>}
        <LinkList text={service.link?.urls_c} />
        {service.ModifiedOn && (
          <p className="mt-1 text-xs text-muted-foreground">Updated {formatRelativeTime(service.ModifiedOn)}</p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <div className="w-full sm:w-52">
          <PickSelect
            id={`st-row-${service.Id}`}
            value={service.status_c || 'Up'}
            onChange={changeStatus}
            values={STATUS_VALUES}
            labels={STATUS_LABELS}
            disabled={saving}
            className={cn(meta.badge, 'border')}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => onEdit(service)}>
          <ApperIcon name="Pencil" size={16} />
          Edit
        </Button>
        <ConfirmDelete
          title={`Delete ${service.Name}?`}
          description="It disappears from the status page and its saved links are removed."
          onConfirm={remove}
        />
      </div>
    </li>
  );
}

export default function AdminStatus() {
  const { data, loading, error, run } = useFetch(loadAdminStatus, []);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const services = data ?? [];
  const shown = services.filter((s) => s.active_c !== false);
  const overall = overallOf(shown);
  const nextSort = (services.reduce((m, s) => Math.max(m, Number(s.sort_c) || 0), 0) || 0) + 10;

  function edit(service) {
    setEditing(service);
    setOpen(true);
  }

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow="Store"
        title="Status page"
        description="Pick a status for each service and the public status page and the banner on your home page update straight away."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to={STATUS_PATH} target="_blank" rel="noreferrer">
                <ApperIcon name="ExternalLink" size={16} />
                View status page
              </Link>
            </Button>
            <Button onClick={() => edit(null)}>
              <ApperIcon name="Plus" size={16} />
              Add service
            </Button>
          </>
        }
      />

      {overall && (
        <div className={cn('mb-4 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm', metaOf(overall).badge)}>
          <ApperIcon name={metaOf(overall).icon} size={18} className="shrink-0" />
          <p>
            <span className="font-medium">Visitors see: {metaOf(overall).headline}.</span>{' '}
            <span className="text-foreground/75">The worst status among shown services sets the headline.</span>
          </p>
        </div>
      )}

      <Panel bodyClassName="py-1">
        <ListBody
          loading={loading}
          data={data}
          error={error}
          run={run}
          rows={4}
          isEmpty={!services.length}
          empty={{
            icon: 'Activity',
            title: 'No services yet',
            description: 'Add the parts of Ghost Cable you want visitors to see, like Live channels, The Vault, Program guide or Apps.',
            action: () => edit(null),
            actionLabel: 'Add service',
          }}
        >
          <ul className="divide-y divide-border">
            {services.map((s) => (
              <ServiceRow key={s.Id} service={s} onEdit={edit} onChanged={run} />
            ))}
          </ul>
        </ListBody>
      </Panel>

      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        <ApperIcon name="Lock" size={12} />
        Links are stored separately and only you and staff can read them. The public page never loads them.
      </p>

      <ServiceDialog open={open} onOpenChange={setOpen} service={editing} nextSort={nextSort} onSaved={run} />
    </div>
  );
}
